import React, {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import BWModels from "@benjamin-west-rpm2/benjamin-west-models";
import { withRouter } from "react-router";
import get from "lodash.get";

import { FormikForm, ActionButtons, FormGrid } from "../../forms";
import {
  calculateOverageQuantity,
  calculateTotalQuantity,
} from "./calculations";
import { roundToCents, add } from "../../../utils/mathUtils";
import styled from "styled-components";
import propTypes from "../../../constants/propTypes";
import { useV2DatacomponentResources } from "../../hooks/useResources";
import * as RequestTypes from "../../../constants/RequestTypes";
import { validateForm } from "../../forms/FormikForm";
import { useIsProjectClosed } from "../../hooks/useIsProjectClosed";
import { useDatacomponent, useLoading } from "../../hooks/useDatacomponent";
import { dataComponentId } from "./CreateSpec/CreateSpecContainer";
import { useCreateProcessRequests } from "../../hooks/useProcessRequest";
import { LoaderContext } from "../../ui/Loader";
import useScope from "../../hooks/useScope";

const defaultCalculatedFieldValue = "--";

const Wrapper = styled.div`
  max-height: calc(65vh - 72px);
  overflow-y: auto;
  margin-bottom: 24px;
`;

export const handleBaseQuantityChange = (
  setBaseQuantity,
  setFieldValue,
  overagePercent,
  atticStock
) => event => {
  const value = parseFloat(event.target.value);

  if (isNaN(value)) {
    setBaseQuantity(0);
    setFieldValue("baseQuantity", null);
    return;
  }

  setBaseQuantity(value);

  const overageValue = calculateOverageQuantity(
    overagePercent,
    value,
    atticStock
  );

  setFieldValue("overageValue", overageValue);

  setFieldValue(
    "totalQuantity",
    calculateTotalQuantity(value, atticStock, overageValue)
  );
};

export const handleAtticStockChange = (
  setAtticStock,
  setFieldValue,
  baseQuantity,
  overagePercent
) => event => {
  const value = parseFloat(event.target.value);

  if (isNaN(value)) {
    setAtticStock(0);
    setFieldValue("atticStock", null);
    return;
  }
  setAtticStock(value);

  const overageValue = calculateOverageQuantity(
    overagePercent,
    baseQuantity,
    value
  );

  setFieldValue("overageValue", overageValue);

  setFieldValue(
    "totalQuantity",
    calculateTotalQuantity(baseQuantity, value, overageValue)
  );
};

export const handleOverageChange = (
  setOveragePercent,
  setFieldValue,
  baseQuantity,
  atticStock
) => event => {
  const percent = parseFloat(event.target.value) / 100;

  if (isNaN(percent)) {
    setOveragePercent(0);

    setFieldValue("overagePercent", null);
    setFieldValue("overageValue", defaultCalculatedFieldValue);

    setFieldValue(
      "totalQuantity",
      calculateTotalQuantity(baseQuantity, atticStock)
    );

    return;
  }
  setOveragePercent(percent);

  const overageValue = calculateOverageQuantity(
    percent,
    baseQuantity,
    atticStock
  );

  setFieldValue("overageValue", overageValue);

  setFieldValue(
    "totalQuantity",
    calculateTotalQuantity(baseQuantity, atticStock, overageValue)
  );
};

export const handleCurrencyValueChange = (
  setFieldValue,
  selectedProjectCurrency,
  changingCurrency,
  setChangingCurrency
) => (event, fieldName) => {
  if (!selectedProjectCurrency) return;
  if (changingCurrency) {
    setChangingCurrency(false);
    return;
  }
  const value = event.target.value || 0;

  const newFieldNameValue = roundToCents(value);

  setFieldValue(fieldName, newFieldNameValue);
};

/**
 * Add two values with decimal precision
 * @param {number} a - First value
 * @param {number} b - Second value
 * @returns {number} Sum with precise decimal handling
 */
export const addVal = (a, b) => {
  return add(a, b);
};

export const getQuantityErrorMessage = (spec, values) => {
  const totalQuantityShipped = get(spec, "totalQuantityShipped", 0);
  const totalQuantityPaid = get(spec, "totalQuantityPaid", 0);
  const initialBaseQuantity = get(spec, "baseQuantity", 0);
  const initialAtticStock = get(spec, "atticStock", 0);
  const initialOverageValue = get(spec, "overageValue", 0);
  const baseQuantity = get(values, "baseQuantity", initialBaseQuantity);
  const atticStock = get(values, "atticStock", initialAtticStock);
  const overageValue = get(values, "overageValue", initialOverageValue);
  const totalQuantityWithoutOverage = addVal(baseQuantity, atticStock);
  const totalQuantityWithOverage = addVal(
    totalQuantityWithoutOverage,
    isNaN(overageValue) ? 0 : overageValue
  );
  if (totalQuantityWithOverage < totalQuantityPaid) {
    const message = `Total quantity paid is ${totalQuantityPaid}. Base Quantity + Attic Stock + Overage Value has to be equal to or greater than the total quantity paid.`;
    return message;
  }
  if (totalQuantityWithoutOverage < totalQuantityShipped) {
    const message = `Total quantity shipped is ${totalQuantityShipped}. Base Quantity + Attic Stock has to be equal to or greater than the total quantity shipped.`;
    return message;
  }
};

export const validateSpec = (spec, isPatch = false) => values => {
  const validationSchema = BWModels.loadSchema("Spec");

  const errors = validateForm(values, validationSchema, undefined, isPatch);

  const quantityErrorMessage = getQuantityErrorMessage(spec, values);

  if (quantityErrorMessage) {
    errors.baseQuantity = quantityErrorMessage;
    errors.atticStock = quantityErrorMessage;
  }

  return errors;
};

const noopChange = () => null;

// BW-7020 follow-up: this lives in its own component so the field tree can be
// memoized. It used to be built inline in Formik's render prop, where hooks are
// not allowed, so every keystroke rebuilt every group, input config, APIOptions
// object and the <QuantityError/> element — and re-rendered all ~15 inputs,
// autocompletes included.
function SpecFormBody({
  formikProps,
  handleSubmit,
  values,
  errors,
  setFieldValue,
  quantities,
  spec,
  createFields,
  clientId,
  scopeId,
  projectId,
  disableVendorField,
  isProjectClosed,
  selectedCurrency,
  quantityErrorMessage,
  formRef,
  buildSubmit,
  sendButtonText,
  saveAndCreateAnotherText,
  cancelButtonText,
  onCancel,
  additionalProps,
  loadingSave,
  loading,
  listeners,
}) {
  const {
    baseQuantity,
    atticStock,
    overagePercent,
    setBaseQuantity,
    setAtticStock,
    setOveragePercent,
  } = quantities;

  // Each handler closes over the current quantities, so it has to be rebuilt
  // when they change — but only then, not on every keystroke in another field.
  const onBaseQuantityChange = useMemo(
    () =>
      handleBaseQuantityChange(
        setBaseQuantity,
        setFieldValue,
        overagePercent,
        atticStock
      ),
    [setBaseQuantity, setFieldValue, overagePercent, atticStock]
  );

  const onAtticStockChange = useMemo(
    () =>
      handleAtticStockChange(
        setAtticStock,
        setFieldValue,
        baseQuantity,
        overagePercent
      ),
    [setAtticStock, setFieldValue, baseQuantity, overagePercent]
  );

  const onOverageChange = useMemo(
    () =>
      handleOverageChange(
        setOveragePercent,
        setFieldValue,
        baseQuantity,
        atticStock
      ),
    [setOveragePercent, setFieldValue, baseQuantity, atticStock]
  );

  const fields = useMemo(
    () =>
      createFields({
        clientId,
        scopeId,
        projectId,
        onBaseQuantityChange,
        handleAtticStockChange: onAtticStockChange,
        handleOverageChange: onOverageChange,
        handleSpecCategoryChange: noopChange,
        selectedCurrency,
        disableVendorField,
        spec,
        quantityErrorMessage,
        isProjectClosed,
      }),
    [
      createFields,
      clientId,
      scopeId,
      projectId,
      onBaseQuantityChange,
      onAtticStockChange,
      onOverageChange,
      selectedCurrency,
      disableVendorField,
      spec,
      quantityErrorMessage,
      isProjectClosed,
    ]
  );

  return (
    <Fragment>
      <Wrapper ref={formRef}>
        <FormGrid
          fields={fields}
          purchaseOrder={spec && spec.purchaseOrder}
          values={values}
          errors={errors}
          {...formikProps}
        />
      </Wrapper>
      <ActionButtons
        onSend={buildSubmit(handleSubmit, false, formRef, quantityErrorMessage)}
        sendButtonText={sendButtonText}
        onSecondary={buildSubmit(
          handleSubmit,
          true,
          formRef,
          quantityErrorMessage
        )}
        secondaryButtonText={saveAndCreateAnotherText}
        cancelButtonText={cancelButtonText}
        onCancel={onCancel}
        isModal={true}
        additionalProps={{
          ...additionalProps,
          secondary: {
            ...get(additionalProps, "secondary", {}),
            isLoading:
              get(additionalProps, "secondary.isLoading", false) ||
              loadingSave ||
              loading,
          },
        }}
        loading={loadingSave}
        listeners={listeners}
      />
    </Fragment>
  );
}

SpecFormBody.propTypes = {
  formikProps: PropTypes.shape({}),
  handleSubmit: PropTypes.func,
  values: PropTypes.shape({}),
  errors: PropTypes.shape({}),
  setFieldValue: PropTypes.func,
  quantities: PropTypes.shape({
    baseQuantity: PropTypes.number,
    atticStock: PropTypes.number,
    overagePercent: PropTypes.number,
    setBaseQuantity: PropTypes.func,
    setAtticStock: PropTypes.func,
    setOveragePercent: PropTypes.func,
  }).isRequired,
  spec: PropTypes.shape({}),
  createFields: PropTypes.func.isRequired,
  clientId: PropTypes.string,
  scopeId: PropTypes.string,
  projectId: PropTypes.string,
  disableVendorField: PropTypes.bool,
  isProjectClosed: PropTypes.bool,
  selectedCurrency: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({}),
  ]),
  quantityErrorMessage: PropTypes.string,
  formRef: PropTypes.shape({}),
  buildSubmit: PropTypes.func.isRequired,
  sendButtonText: PropTypes.string,
  saveAndCreateAnotherText: PropTypes.string,
  cancelButtonText: PropTypes.string,
  onCancel: PropTypes.func,
  additionalProps: propTypes.buttonAdditionalProps,
  loadingSave: PropTypes.bool,
  loading: PropTypes.bool,
  listeners: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
};

function SpecFormikForm(props) {
  const {
    spec,
    initialValues,
    onSubmit,
    buildSubmit,
    validationSchema,
    createFields,
    disableVendorField,
    sendButtonText,
    saveAndCreateAnotherText,
    cancelButtonText,
    onCancel,
    additionalProps = {},
    loadingSave,
    projectCurrency,
    match,
    listeners,
  } = props;
  const [baseQuantity, setBaseQuantity] = useState(spec.baseQuantity || 0);
  const [atticStock, setAtticStock] = useState(spec.atticStock || 0);
  const [overagePercent, setOveragePercent] = useState(
    spec.overagePercent / 100 || 0
  );

  const projectCurrencies = useV2DatacomponentResources(
    "select-project-currencies",
    [],
    RequestTypes.LIST
  );

  const isProjectClosed = useIsProjectClosed();
  const clientId = match.params.clientId;
  const scope = useScope();

  const dataComponent = useDatacomponent(dataComponentId);
  useCreateProcessRequests(dataComponent, {
    onSuccess: () => setOveragePercent(0),
  });

  useEffect(() => {
    setBaseQuantity(spec.baseQuantity || 0);
    setAtticStock(spec.atticStock || 0);
    setOveragePercent(spec.overagePercent / 100 || 0);
  }, [spec]);

  const ref = useRef(null);

  // useState setters are stable, so this only changes when a quantity does.
  const quantities = useMemo(
    () => ({
      baseQuantity,
      atticStock,
      overagePercent,
      setBaseQuantity,
      setAtticStock,
      setOveragePercent,
    }),
    [baseQuantity, atticStock, overagePercent]
  );

  const { loading: loadingContext } = useContext(LoaderContext);
  const loading = useLoading(listeners) || loadingContext;

  return (
    <LoaderContext.Provider value={{ loading }}>
      <FormikForm
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        enableReinitialize
        ignoreCache
      >
        {({ handleSubmit, values, errors, setFieldValue, ...formikProps }) => {
          const selectedProjectCurrency =
            projectCurrency ||
            projectCurrencies.find(
              projectCurrency => projectCurrency.id == values.projectCurrencyId
            );

          const quantityErrorMessage =
            getQuantityErrorMessage(spec, values) || errors.totalQuantityError;

          return (
            <SpecFormBody
              formikProps={formikProps}
              handleSubmit={handleSubmit}
              values={values}
              errors={errors}
              setFieldValue={setFieldValue}
              quantities={quantities}
              spec={spec}
              createFields={createFields}
              clientId={clientId}
              scopeId={scope?.id}
              projectId={initialValues.projectId}
              disableVendorField={disableVendorField}
              isProjectClosed={isProjectClosed}
              selectedCurrency={selectedProjectCurrency?.currency}
              quantityErrorMessage={quantityErrorMessage}
              formRef={ref}
              buildSubmit={buildSubmit}
              sendButtonText={sendButtonText}
              saveAndCreateAnotherText={saveAndCreateAnotherText}
              cancelButtonText={cancelButtonText}
              onCancel={onCancel}
              additionalProps={additionalProps}
              loadingSave={loadingSave}
              loading={loading}
              listeners={listeners}
            />
          );
        }}
      </FormikForm>
    </LoaderContext.Provider>
  );
}
SpecFormikForm.defaultProps = {
  spec: {},
  initialValues: {},
  onSubmit: () => {},
  buildSubmit: () => {},
  validationSchema: {},
  createFields: () => {},
  disableVendorField: false,
  sendButtonText: "Send",
  saveAndCreateAnotherText: "Save & Next",
  onCancel: () => {},
  cancelButtonText: "Cancel",
  additionProps: {},
};
SpecFormikForm.propTypes = {
  spec: PropTypes.shape({}),
  initialValues: PropTypes.shape({}),
  onSubmit: PropTypes.func.isRequired,
  buildSubmit: PropTypes.func.isRequired,
  createFields: PropTypes.func.isRequired,
  disableVendorField: PropTypes.bool,
  sendButtonText: PropTypes.string.isRequired,
  saveAndCreateAnotherText: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  cancelButtonText: PropTypes.string,
  additionalProps: propTypes.buttonAdditionalProps,
  projectCurrency: PropTypes.shape({}),
  validationSchema: PropTypes.object,
  loadingSave: PropTypes.bool,
  match: PropTypes.shape({ params: PropTypes.shape({}) }),
  listeners: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
};
export default withRouter(SpecFormikForm);
